import { useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Paperclip, FileText } from "lucide-react";

/**
 * Wraps a trigger element (the attachments icon/button) and shows
 * a hover preview popover containing the list of attachments.
 *
 * Props:
 *  - endpoint: API endpoint string to fetch the attachments list (e.g. `/clients/123/documents`)
 *  - count: pre-known attachments count (used to skip the fetch when zero)
 *  - children: the trigger element (button/icon)
 *  - placement: "left" | "right" (default "left" => popover aligned right edge)
 *  - testid: optional data-testid for the popover
 */
export function AttachmentsHoverPreview({ endpoint, count = 0, children, placement = "left", testid }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hideTimer = useRef(null);
  const fetchedRef = useRef(false);

  const cancelHide = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const handleEnter = useCallback(async () => {
    cancelHide();
    setOpen(true);
    if (fetchedRef.current || count === 0) return;
    fetchedRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(endpoint);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Impossibile caricare gli allegati");
      fetchedRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [endpoint, count]);

  const popoverAlign = placement === "right" ? "left-0" : "right-0";

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={handleEnter}
      onMouseLeave={scheduleHide}
    >
      {children}
      {open && (
        <div
          data-testid={testid || "attachments-hover-preview"}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          className={`absolute top-full mt-1.5 ${popoverAlign} z-50 w-72 bg-white border border-slate-200 rounded-lg shadow-xl p-3 text-left`}
          style={{ animation: "fadeIn 120ms ease-out" }}
        >
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-2 pb-1.5 border-b border-slate-100">
            <Paperclip className="h-3.5 w-3.5 text-blue-600" />
            Allegati ({count})
          </div>
          {count === 0 ? (
            <p className="text-xs text-slate-400 italic">Nessun allegato.</p>
          ) : loading ? (
            <p className="text-xs text-slate-500">Caricamento…</p>
          ) : error ? (
            <p className="text-xs text-red-500">{error}</p>
          ) : !items || items.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Nessun allegato.</p>
          ) : (
            <ul className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {items.map((a) => {
                const name = a.original_filename || a.filename || "Allegato";
                return (
                  <li key={a.id} className="flex items-start gap-1.5 text-xs text-slate-700">
                    <FileText className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium" title={name}>{name}</div>
                      {a.description && (
                        <div className="truncate text-[10px] text-slate-400" title={a.description}>{a.description}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </span>
  );
}

export default AttachmentsHoverPreview;
